/**
 * Tracks whether the customer's purchase is one-time (OTP) or subscription (sub),
 * across three entry points:
 *   - page.otp-only template      -> always "otp"
 *   - page.subscription-only tpl  -> always "sub"
 *   - normal PDP                  -> whatever the Recharge widget has selected
 *
 * Keeps a `?purchase-type=otp|sub` URL param in sync with the current selection
 * (via history.replaceState, no reload). No cart/checkout hand-off here - the
 * Meta pixel derives purchase type itself from selling plan IDs on the checkout's
 * line items, so this file only needs to manage the URL param for on-page use.
 *
 * Recharge widget structure (confirmed from live markup):
 *   <recharge-subscription-widget>
 *     #shadow-root (declarative, mode "open")
 *       <input type="radio" name="purchaseOption" value="subscription">
 *       <input type="radio" name="purchaseOption" value="onetime">
 * That shadow root may or may not be auto-attached depending on how Recharge
 * inserts the widget into the page (declarative shadow DOM only auto-attaches
 * during initial HTML parsing, not when injected via innerHTML) - getShadowRoot()
 * below handles both cases.
 */
(function () {
  'use strict';

  var PARAM_NAME = 'purchase-type';
  var OTP_VALUE = 'otp';
  var SUB_VALUE = 'sub';

  var PURCHASE_OPTION_INPUT_NAME = 'purchaseOption';
  var SUBSCRIPTION_INPUT_VALUE = 'subscription';
  var ONE_TIME_INPUT_VALUE = 'onetime';

  function findRechargeWidget() {
    return document.querySelector('recharge-subscription-widget');
  }

  // Returns a usable shadow root for `el`, manually promoting an inert
  // declarative <template shadowrootmode> into a real shadow root if the
  // browser didn't already do it automatically.
  function getShadowRoot(el) {
    if (!el) return null;
    if (el.shadowRoot) return el.shadowRoot;
    var template = el.querySelector(':scope > template[shadowrootmode]');
    if (!template) return null;
    try {
      var shadowRoot = el.attachShadow({ mode: template.getAttribute('shadowrootmode') || 'open' });
      shadowRoot.appendChild(template.content);
      template.remove();
      return shadowRoot;
    } catch (error) {
      return null;
    }
  }

  function getPurchaseOptionInputs() {
    var widget = findRechargeWidget();
    if (!widget) return [];
    var root = getShadowRoot(widget) || widget;
    return Array.prototype.slice.call(
      root.querySelectorAll('input[name="' + PURCHASE_OPTION_INPUT_NAME + '"]')
    );
  }

  function classifyPurchaseOptionValue(value) {
    if (value === SUBSCRIPTION_INPUT_VALUE) return SUB_VALUE;
    if (value === ONE_TIME_INPUT_VALUE) return OTP_VALUE;
    return null;
  }

  function detectWidgetSelection() {
    var inputs = getPurchaseOptionInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) {
        var type = classifyPurchaseOptionValue(inputs[i].value);
        if (type) return type;
      }
    }
    return null;
  }

  function selectWidgetOption(type) {
    var inputs = getPurchaseOptionInputs();
    var targetValue = type === SUB_VALUE ? SUBSCRIPTION_INPUT_VALUE : ONE_TIME_INPUT_VALUE;
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === targetValue) {
        inputs[i].click();
        return true;
      }
    }
    return false;
  }

  function getUrlParam() {
    var value = new URLSearchParams(window.location.search).get(PARAM_NAME);
    return value === OTP_VALUE || value === SUB_VALUE ? value : null;
  }

  function setUrlParam(type) {
    if (getUrlParam() === type) return;
    var url = new URL(window.location.href);
    url.searchParams.set(PARAM_NAME, type);
    window.history.replaceState(window.history.state, '', url);
  }

  function initSelectablePdp() {
    var urlType = getUrlParam();

    if (urlType) {
      var applyAttempts = 0;
      (function tryApplyFromUrl() {
        applyAttempts += 1;
        if (selectWidgetOption(urlType) || applyAttempts > 20) return;
        window.setTimeout(tryApplyFromUrl, 250);
      })();
    } else {
      // No param yet - reflect whatever the widget defaults to once it's
      // finished rendering (it's injected asynchronously, so it may not exist
      // yet on the first attempt).
      var detectAttempts = 0;
      (function tryDetectInitial() {
        detectAttempts += 1;
        var initial = detectWidgetSelection();
        if (initial) {
          setUrlParam(initial);
          return;
        }
        if (detectAttempts <= 20) window.setTimeout(tryDetectInitial, 250);
      })();
    }

    // `change` does not cross shadow DOM boundaries per spec - `click` does,
    // and composedPath() lets us find the actual <input> even though it lives
    // inside the widget's shadow root.
    document.addEventListener('click', function (event) {
      var path = event.composedPath();
      var input = null;
      for (var i = 0; i < path.length; i++) {
        if (path[i] && path[i].tagName === 'INPUT' && path[i].name === PURCHASE_OPTION_INPUT_NAME) {
          input = path[i];
          break;
        }
      }
      if (!input) return;
      var type = classifyPurchaseOptionValue(input.value);
      if (type) setUrlParam(type);
    });
  }

  function init() {
    var productInfo = document.querySelector('product-info[data-purchase-type-mode]');
    if (!productInfo) return;

    var mode = productInfo.getAttribute('data-purchase-type-mode');

    if (mode === 'otp' || mode === 'sub') {
      setUrlParam(mode);
      return;
    }

    initSelectablePdp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
